import { merge } from 'lodash';
import FixedWidth from '../../../common/Themes/FixedWidth/Theme';
import { spacingVertical } from '../../../common/utils';
import * as utils from '../../../common/Themes/Default/Dials/Colors/utils';
import * as filledIcons from '../../../common/IconPacks/humanisticFilled';
import * as socialIconPack from '../../../common/IconPacks/SocialIcons/defaultSocialIconPack';
import { Blink } from '../../../common/loaders';
import { fontSizeByDevice } from '../../../common/Themes/Default/Dials/Fonts/utils/getSizesByDevice';
import { XSMALL, MEDIUM, LARGE } from '../../../common/constants/breakpoints';
import { levelFilter } from '../../../common/utils/text';
import { mergeTypographyOverrides } from '../../../common/utils/typography';
import { FILL, FIT } from '../../../common/constants/headerTreatments';
import themeConfig from '../themeConfig';
import { WIDGETS_WITH_CIRCULAR_IMAGES } from '../../../common/constants';

const promoBannerHover = {
  '@md': {
    'transition': 'filter 0.6s',
    ':hover': {
      filter: 'invert(.2)'
    }
  }
};
const typographyShared1 = {
  style: {
    font: 'primary',
    color: 'highlight',
    fontSize: 'xlarge',
    fontWeight: 'normal',
    letterSpacing: 'normal',
    textTransform: 'none'
  }
};

class Theme25 extends FixedWidth {
  static config = themeConfig;

  static get displayName() {
    return 'Theme25';
  }

  static getMutatorDefaultProps(widgetType, widgetPreset) {
    const defaultProps = super.getMutatorDefaultProps(widgetType, widgetPreset);
    const enableCircularImage =
      WIDGETS_WITH_CIRCULAR_IMAGES[widgetPreset] || defaultProps.enableCircularImage;

    return widgetType === 'HEADER'
      ? {
        ...defaultProps,
        hasLogoAlign: true,
        headerTreatmentsConfig: {
          ...defaultProps.headerTreatmentsConfig,
          headerTreatments: [FILL, FIT],
          imageTreatments: {
            [FILL]: 'none',
            [FIT]: 'none'
          }
        },
        renderParallaxToggle: false
      }
      : {
        ...defaultProps,
        enableCircularImage
      };
  }

  constructor() {
    super();
    this.mappedValues = {
      ...this.mappedValues,
      backgroundColorInput() {
        const [background] = utils.getDial(this, ['background']);
        return background.setAlpha(100).setLightness(95);
      },
      fontSizeMapping: merge({}, fontSizeByDevice, {
        [XSMALL]: {
          xlarge: 24
        },
        [MEDIUM]: {
          xlarge: 28
        },
        [LARGE]: {
          xlarge: 30
        }
      }),
      typographyOverrides: {
        LogoAlpha: {
          ...typographyShared1
        },
        HeadingAlpha: {
          style: {
            font: 'primary',
            color: 'highlight',
            fontSize: 'xxlarge',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        HeadingBeta: {
          style: {
            font: 'alternate',
            color: 'highContrast',
            fontSize: 'medium',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        HeadingGamma: {
          ...typographyShared1
        },
        HeadingDelta: props => {
          return mergeTypographyOverrides.call(this, 'HeadingGamma', props);
        },
        BodyBeta: {
          style: {
            font: 'alternate',
            color: 'section',
            fontSize: 'large',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        DetailsAlpha: {
          style: {
            font: 'alternate',
            color: 'lowContrast',
            fontSize: 'medium',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        NavAlpha: () => {
          const { category } = this.base;
          const color = category === 'accent' ? 'highContrast' : 'highlight';
          const colorActive = category === 'accent' ? 'highlight' : 'highContrast';
          return {
            style: {
              font: 'alternate',
              color,
              fontSize: 'medium',
              fontWeight: 'normal',
              letterSpacing: 'normal',
              textTransform: 'none',
              [':hover']: {
                color: 'highlightHover'
              }
            },
            active: {
              style: {
                color: colorActive,
                fontWeight: 'normal'
              }
            }
          };
        },
        SubNavAlpha: {
          style: {
            font: 'alternate',
            color: 'section',
            fontSize: 'medium',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none',
            [':hover']: {
              color: 'highlightHover'
            }
          },
          active: {
            style: {
              fontWeight: 'bold',
              color: 'section',
              [':hover']: {
                color: 'highlightHover'
              }
            }
          }
        },
        NavBeta: {
          style: {
            font: 'alternate',
            color: 'highContrast',
            fontSize: 'xlarge',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none',
            [':hover']: {
              color: 'highlightHover'
            },
            [':active']: {
              color: 'highlight'
            }
          },
          active: {
            style: {
              fontWeight: 'normal',
              color: 'highlight'
            }
          }
        },
        SubNavBeta: {
          active: {
            style: {
              fontWeight: 'normal',
              color: 'highlight'
            }
          }
        },
        ButtonAlpha: props => {
          const { size = 'default' } = props;
          const sizes = {
            small: {
              fontSize: 'small'
            },
            default: {
              fontSize: 'mlarge'
            },
            large: {
              fontSize: 'mlarge'
            }
          };
          return {
            style: {
              font: 'alternate',
              fontWeight: 'bold',
              letterSpacing: 'normal',
              textTransform: 'none',
              ...sizes[size]
            }
          };
        },
        InputBeta: props => mergeTypographyOverrides.call(this, 'BodyAlpha', props)
      }
    };
  }

  fixedHeading = {
    typography: 'BodyBeta',
    featured: true
  };

  Heading(props) {
    const { level, tag } = props;
    const typographyMapping = {
      2: 'HeadingAlpha'
    };

    return super.Heading(
      this.merge(
        {
          typography: typographyMapping[levelFilter({ tag, level })]
        },
        props
      )
    );
  }

  HeadingMajor(props) {
    return super.HeadingMajor(
      this.merge(
        {
          typography: 'HeadingAlpha'
        },
        props
      )
    );
  }

  HeadingProduct(props) {
    return this.HeadingMinor(
      this.merge(
        {
          ...this.fixedHeading
        },
        props
      )
    );
  }

  Text(props) {
    return super.Text(
      this.merge(
        {
          style: {
            lineHeight: 1.8
          }
        },
        props
      )
    );
  }

  Section(props) {
    const overrideStyle =
      this.base.widgetType === 'HEADER'
        ? { padding: '0 !important' }
        : { paddingVertical: 'large' };
    return super.Section(
      this.merge(
        {
          style: {
            '@xs-only': {
              paddingVertical: 'medium'
            },
            ...overrideStyle
          }
        },
        props
      )
    );
  }

  SectionHeading(props) {
    return super.SectionHeading(
      this.merge(
        {
          sectionHeadingHR: 'INLINE',
          style: {
            textAlign: 'center',
            marginLeft: 'auto',
            marginBottom: 'medium',
            ['@md']: {
              textAlign: 'center',
              marginLeft: 'auto',
              marginBottom: 'large'
            },
            ['> div > span:after']: {
              color: 'ultraLowContrast'
            },
            ['> div > span:before']: {
              color: 'ultraLowContrast'
            }
          }
        },
        props
      )
    );
  }

  Intro(props) {
    return super.Intro(
      this.merge(
        {
          style: {
            marginBottom: 'medium',
            ['@md']: {
              marginBottom: 'large'
            }
          },
          alignment: 'center'
        },
        props
      )
    );
  }

  /* Header */

  NavLink(props) {
    return super.NavLink(
      this.merge(props, {
        style: {
          paddingBottom: 'xxsmall'
        }
      })
    );
  }

  NavLinkActive(props) {
    const { category } = this.base;
    const borderColor = category === 'accent' ? 'highlight' : 'highContrast';
    const paddingOffset = this.parseSizedUnit(this.mapPropValue('padding', '-xxsmall'))[0];
    const marginBottom = paddingOffset - 2;
    return this.NavLink(
      this.merge(
        {
          style: {
            borderColor,
            paddingHorizontal: 0,
            borderBottom: '2px solid',
            marginBottom
          },
          active: true
        },
        props
      )
    );
  }

  NavMoreMenuLink(props) {
    return super.NavMoreMenuLink(
      this.merge(
        {
          style: {
            marginVertical: 'xxsmall'
          }
        },
        props
      )
    );
  }

  NavMoreMenuLinkActive(props) {
    return super.NavMoreMenuLinkActive(
      this.merge(
        {
          style: {
            marginBottom: 'medium'
          }
        },
        props
      )
    );
  }

  Hero(props) {
    return super.Hero(
      this.merge(
        {
          style: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            paddingTop: 'xxlarge',
            paddingBottom: 'large',
            width: '100%',
            ['@xs-only']: {
              paddingTop: 'xlarge',
              paddingBottom: 'medium'
            },
            ...spacingVertical('medium')
          }
        },
        props
      )
    );
  }

  HeroRight(props) {
    return this.Hero(
      this.merge(
        {
          style: {
            alignItems: 'flex-end'
          }
        },
        props
      )
    );
  }

  HeroLeft(props) {
    return this.Hero(
      this.merge(
        {
          style: {
            alignItems: 'flex-start'
          }
        },
        props
      )
    );
  }

  HeroLink(props) {
    return this.Link(props);
  }

  Tagline(props) {
    return super.Tagline(
      this.merge(
        {
          style: {
            textAlign: 'center',
            overflowWrap: 'break-word',
            wordWrap: 'break-word',
            maxWidth: '100%'
          },
          typography: 'HeadingGamma'
        },
        props
      )
    );
  }

  HeroRightHeading(props) {
    return this.HeroHeading(
      this.merge(
        {
          style: {
            textAlign: 'right'
          }
        },
        props
      )
    );
  }

  HeroLeftHeading(props) {
    return this.HeroHeading(
      this.merge(
        {
          style: {
            textAlign: 'left'
          }
        },
        props
      )
    );
  }

  SubTagline(props) {
    return super.SubTagline(
      this.merge(
        {
          typography: 'BodyAlpha',
          featured: false
        },
        props
      )
    );
  }

  HeroText(props) {
    return this.SubTagline(
      this.merge(
        {
          style: {
            textAlign: 'center'
          }
        },
        props
      )
    );
  }

  HeroRightText(props) {
    return this.HeroText(
      this.merge(
        {
          style: {
            textAlign: 'right'
          }
        },
        props
      )
    );
  }

  HeroLeftText(props) {
    return this.HeroText(
      this.merge(
        {
          style: {
            textAlign: 'left'
          }
        },
        props
      )
    );
  }

  PromoBanner(props) {
    return super.PromoBanner(
      this.merge(props, {
        style: {
          ['@md']: {
            transition: 'none',
            [':hover']: {
              filter: 'none'
            }
          }
        }
      })
    );
  }

  PromoBannerContainer(props) {
    return super.PromoBannerContainer(
      this.merge(
        {
          style: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            ['> :first-child']: {
              textAlign: 'left'
            },
            ['> :last-child']: {
              textAlign: 'right'
            },
            ['> :only-child']: {
              textAlign: 'center',
              marginHorizontal: 'auto'
            },
            ['@xs-only']: {
              flexDirection: 'column',
              ['> :first-child']: {
                textAlign: 'center'
              },
              ['> :last-child']: {
                textAlign: 'center'
              }
            }
          }
        },
        props
      )
    );
  }

  PromoBannerSeasonalContainer(props) {
    return super.PromoBannerContainer(
      this.merge(
        {
          style: {
            flexWrap: 'wrap',
            ['> a']: {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }
          }
        },
        props
      )
    );
  }

  PromoBannerSeasonalPhone(props) {
    return this.Phone(
      this.merge(
        {
          style: {
            display: 'block',
            width: '100%',
            textAlign: 'center',
            ['> a']: {
              ...promoBannerHover
            }
          }
        },
        props
      )
    );
  }

  PromoBannerText({ hasLink, ...props }) {
    return super.PromoBannerText(
      this.merge(
        {
          style: {
            ...(hasLink && promoBannerHover)
          }
        },
        props
      )
    );
  }

  ImageLogo(props) {
    return super.ImageLogo(
      this.merge(
        {
          style: {
            '@xs-only': {
              width: 'auto'
            }
          }
        },
        props
      )
    );
  }

  /* Mobile Overlay */

  NavigationDrawer(props) {
    return super.NavigationDrawer(
      this.merge(
        {
          category: 'neutral',
          section: 'default',
          style: {
            backgroundColor: 'section'
          }
        },
        props
      )
    );
  }

  NavigationDrawerInputSearch(props) {
    return super.NavigationDrawerInputSearch(
      this.merge(
        {
          style: {
            flexGrow: 1,
            position: 'relative',
            width: 'auto',
            backgroundColor: 'searchMobileLight'
          }
        },
        props
      )
    );
  }

  NavigationDrawerContainer(props) {
    return super.Container(
      this.merge(
        {
          style: {
            padding: 'large'
          }
        },
        props
      )
    );
  }

  NavigationDrawerLink(props) {
    return super.NavigationDrawerLink(
      this.merge(props, {
        style: {
          paddingHorizontal: 'medium'
        }
      })
    );
  }

  NavigationDrawerListItem(props) {
    return super.NavigationDrawerListItem(
      this.merge(
        {
          style: {
            borderBottomWidth: 0
          }
        },
        props
      )
    );
  }

  NavigationDrawerIcon(props) {
    return super.Icon(
      this.merge(
        {
          style: {
            ...(props.icon === 'magGlass'
              ? {
                'left': 8,
                'color': 'input',
                '@md': {
                  position: 'absolute',
                  top: 'large',
                  right: 0
                }
              }
              : {})
          }
        },
        props
      )
    );
  }

  FormSearch(props) {
    const mobileSearchStyle = {
      flexGrow: 1,
      display: 'flex'
    };
    return super.FormSearch(
      this.merge(
        {
          style: {
            ['@xs-only']: mobileSearchStyle,
            ['@sm-only']: mobileSearchStyle
          }
        },
        props
      )
    );
  }

  Phone(props) {
    return super.Phone(
      this.merge(
        {
          style: {
            ['> a']: {
              ...promoBannerHover
            }
          },
          typography: 'BodyAlpha'
        },
        props
      )
    );
  }

  Pipe(props) {
    return super.Pipe(
      this.merge(
        {
          style: {
            backgroundColor: 'sectionUltraLowContrast',
            height: '0.8em'
          }
        },
        props
      )
    );
  }

  Button(props) {
    return super.Button(
      this.merge(
        {
          style: {
            marginVertical: 'xxsmall'
          }
        },
        props
      )
    );
  }

  Input(props) {
    return super.Input(
      this.merge(
        {
          style: {
            borderWidth: 'xsmall',
            paddingVertical: 'small',
            paddingHorizontal: 'small',
            borderStyle: 'solid',
            borderColor: 'transparent',
            borderRadius: 0
          }
        },
        props
      )
    );
  }

  InputFloatLabelLabel(props) {
    return super.InputFloatLabelLabel(
      this.merge(
        {
          style: {
            left: '16px',
            top: '33%'
          }
        },
        props
      )
    );
  }

  InputFloatLabelInput(props) {
    return super.InputFloatLabelInput(
      this.merge(
        {
          style: {
            ':focus + label': {
              top: 5
            }
          }
        },
        props
      )
    );
  }

  Icon(props) {
    return super.Icon(
      this.merge(
        {
          iconPack: { ...filledIcons, ...socialIconPack }
        },
        props
      )
    );
  }

  Loader(props) {
    return Blink.apply(this, [props]);
  }

  InputTextArea(props) {
    return super.InputTextArea(
      this.merge(
        {
          rows: 6
        },
        props
      )
    );
  }

  InputSelect(props) {
    return super.InputSelect(
      this.merge(
        {
          style: {
            borderWidth: 'xsmall',
            borderRadius: 'medium',
            borderStyle: 'solid'
          }
        },
        props
      )
    );
  }

  InputSelectElement(props) {
    return super.InputSelectElement(
      this.merge(
        {
          style: {
            paddingVertical: 'small',
            paddingHorizontal: 'small'
          }
        },
        props
      )
    );
  }

  MediaObjectBackground(props) {
    return super.MediaObjectBackground(
      this.merge(
        {
          style: {
            borderRadius: 'medium'
          }
        },
        props
      )
    );
  }

  CardBanner(props) {
    // Force a larger height for empty content 5 so MSH can mimic hero images on secondary pages
    const { heading, action } = props;
    const style =
      !heading && !action && this.base.widgetPreset === 'content5'
        ? {
          minHeight: 440,
          justifyContent: 'center',
          ['@xs-only']: {
            minHeight: 160
          },
          ['> *']: {
            flexGrow: 0
          }
        }
        : {};

    return super.CardBanner(this.merge({ style }, props));
  }

  CardHeading(props) {
    const { widgetPreset } = this.base;

    return super.CardHeading(
      this.merge(
        {
          ...(widgetPreset === 'rss1' && {
            typography: 'BodyAlpha',
            featured: true
          }),
          ...this.fixedHeading
        },
        props
      )
    );
  }

  MapBanner(props) {
    return super.MapBanner(
      this.merge(
        {
          style: {
            ['@md']: {
              marginBottom: 'large'
            }
          }
        },
        props
      )
    );
  }

  WidgetBanner(props) {
    const { isLastPageWidget } = props;
    const style = isLastPageWidget
      ? {
        paddingBottom: 'large',
        ['@xs-only']: {
          paddingBottom: 'medium'
        }
      }
      : {};
    return super.WidgetBanner(this.merge({ style }, props));
  }

  WidgetSplit(props) {
    const { isLastPageWidget } = props;
    const style = isLastPageWidget
      ? {
        paddingBottom: 'large',
        ['@xs-only']: {
          paddingBottom: 'medium'
        }
      }
      : {};
    return super.WidgetSplit(this.merge({ style }, props));
  }

  FeaturedHeading(props) {
    return this.HeroHeading(this.merge({ tag: 'h4' }, props));
  }

  NavFooterLink(props) {
    return super.NavFooterLink(
      this.merge(
        {
          typography: 'LinkAlpha'
        },
        props
      )
    );
  }

  UtilitiesMenuLink(props) {
    return super.UtilitiesMenuLink(
      this.merge(
        {
          featured: true
        },
        props
      )
    );
  }

  UtilitiesMenuIcon(props) {
    return super.UtilitiesMenuIcon(
      this.merge(
        {
          featured: true
        },
        props
      )
    );
  }

  ContentBigText(props) {
    return super.ContentBigText(
      this.merge(
        {
          style: {
            lineHeight: this.mapPropValue('lineHeightLevel', 4)
          }
        },
        props
      )
    );
  }

  NavVerticalHeading(props) {
    return super.NavVerticalHeading(
      this.merge(
        {
          ...this.fixedHeading
        },
        props
      )
    );
  }

  ContentCard(props) {
    const styles = this.base.widgetPreset === 'about1' ? { style: { alignItems: 'center' } } : {};
    return super.ContentCard(this.merge(styles, props));
  }

  InputSearch(props) {
    return super.InputSearch(
      this.merge(
        {
          style: {
            ['::placeholder']: {
              color: 'lowContrast'
            }
          }
        },
        props
      )
    );
  }

  CommerceOverlayHeading(props) {
    return super.CommerceOverlayHeading(
      this.merge(
        {
          typography: 'BodyBeta'
        },
        props
      )
    );
  }

  Image(props) {
    return super.Image(this.merge({ mobileGutterWidth: 24 }, props));
  }

  EmbedVideo({ isVerticalVideo, ...props }) {
    if (isVerticalVideo) {
      return super.EmbedVideo(this.merge({ style: { maxHeight: '680px' } }, props));
    }
    return super.EmbedVideo(props);
  }

  WrapperInsetEmbedVideo({ isVerticalVideo, ...props }) {
    if (isVerticalVideo) {
      return super.WrapperInsetEmbedVideo(this.merge({ style: { maxHeight: '680px' } }, props));
    }
    return super.WrapperInsetEmbedVideo(props);
  }
}

export default Theme25;
